// The geofences this login may see, loaded once and shared by the maps and the address lines
// (a vehicle inside a geofence shows its name above the street address). Refreshed every
// 5 minutes and after any change made on this page. The inside test here is for display only;
// entries and exits are worked out on the server.
import { useEffect, useState } from 'react';
import { Geofence, geofences as api } from '../api';

let list: Geofence[] | null = null;
let loading: Promise<void> | null = null;
let loadedAt = 0;
const listeners = new Set<(g: Geofence[]) => void>();
const MAX_AGE_MS = 5 * 60_000;

function load(force = false): Promise<void> {
  if (!force && list && Date.now() - loadedAt < MAX_AGE_MS) return Promise.resolve();
  if (loading && !force) return loading;
  loading = api.list().then((r) => {
    list = r.geofences;
    loadedAt = Date.now();
    listeners.forEach((fn) => fn(list!));
  }).catch(() => { /* keep what we had; addresses still show without names */ }).finally(() => { loading = null; });
  return loading;
}

// Call after adding, changing or removing a geofence.
export const reloadGeofences = () => load(true);

export function useGeofences(): Geofence[] {
  const [g, setG] = useState<Geofence[]>(list ?? []);
  useEffect(() => {
    listeners.add(setG);
    void load();
    const t = setInterval(() => void load(), MAX_AGE_MS);
    return () => { listeners.delete(setG); clearInterval(t); };
  }, []);
  return g;
}

// Ray casting on [lon, lat]: fine at geofence sizes (≤ 50 km) away from the poles.
export function inside(points: Array<[number, number]>, lat: number, lon: number): boolean {
  let hit = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

// Names of the geofences a position is in, smallest first (the most specific place).
export function geofencesAt(all: Geofence[], lat: number, lon: number): Geofence[] {
  return all.filter((g) => inside(g.points, lat, lon)).sort((a, b) => a.areaM2 - b.areaM2);
}

// Whether maps show geofences: one setting for every map, remembered per browser, changed from
// the Geofences card on the live map.
const SHOWN_KEY = 'ff.map.geofences';
let shown = (() => { try { return localStorage.getItem(SHOWN_KEY) !== '0'; } catch { return true; } })();
const shownListeners = new Set<(on: boolean) => void>();

export function useGeofencesShown(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(shown);
  useEffect(() => { shownListeners.add(setOn); return () => { shownListeners.delete(setOn); }; }, []);
  const set = (v: boolean) => {
    shown = v;
    try { localStorage.setItem(SHOWN_KEY, v ? '1' : '0'); } catch { /* private mode */ }
    shownListeners.forEach((fn) => fn(v));
  };
  return [on, set];
}
